import Phaser from 'phaser';
import {
  ENEMY_MAX_ACTIVE,
  ENEMY_MIN_PLAYER_DISTANCE,
  ENEMY_SPAWN_MARGIN,
  GAME_HEIGHT,
  GAME_WIDTH,
} from '../config/game';
import { BossToken } from '../entities/boss';
import { ENEMY_DEFINITIONS, EnemyDefinition, EnemyKind, EnemyToken } from '../entities/enemy';
import { BossSystem } from './BossSystem';
import type { BossType } from './bossCatalog';
import type { PartnerType } from './shopCatalog';
import type { SeededRandom } from './seededRandom';
import { getTunables } from '../config/tunables';

type BossSpawnMode = 'wave' | 'debug';

export interface EnemyDamagePayload {
  amount: number;
  source?: PartnerType | 'relic';
  x?: number;
  y?: number;
}

export interface EnemyDeathPayload {
  x: number;
  y: number;
  source?: PartnerType | 'relic';
}

type SpawnPoint = {
  x: number;
  y: number;
  side: 'top' | 'right' | 'bottom' | 'left';
};

const BOSS_SCHEDULE: Partial<Record<number, readonly BossType[]>> = {
  3: ['glutton-slime', 'shadow-weaver', 'blood-furnace'],
  5: ['crystal-titan', 'iron-bastion', 'storm-eye'],
  7: ['hive-heart', 'pendulum-knight', 'mirror-shade'],
  10: ['rift-core'],
};
const ENDLESS_BOSS_POOL: readonly BossType[] = [
  'glutton-slime',
  'crystal-titan',
  'hive-heart',
  'pendulum-knight',
  'shadow-weaver',
  'iron-bastion',
  'storm-eye',
  'blood-furnace',
  'mirror-shade',
  'rift-core',
];

export class EnemySystem {
  private readonly enemies: EnemyToken[] = [];
  private readonly bossSystem: BossSystem;
  private readonly spawnWeights: EnemyKind[] = [];
  private elapsedMs = 0;
  private spawnTimerMs = 0;
  private wave = 1;
  private waveToken = 0;
  private bossSpawnedForWave = 0;
  private eliteSpawnedForWave = 0;
  private eliteDueAtMs = Infinity;
  private debugBossHpMultiplier = 1;
  private debugBossSpeedMultiplier = 1;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly player: Phaser.GameObjects.Arc,
    private readonly rng?: SeededRandom,
  ) {
    this.bossSystem = new BossSystem(this.scene, this.player, {
      getBossFromPool: () => this.getBossFromPool(),
      activateSummonedMinion: (spawnPoint, timeMs) => this.activateSummonedMinion(spawnPoint, timeMs),
      getActiveBossMinionCount: () => this.getActiveBossMinionCount(),
    });

    Object.keys(ENEMY_DEFINITIONS).forEach((kind) => {
      this.spawnWeights.push(kind as EnemyKind);
    });
  }

  update(timeMs: number, deltaMs: number) {
    this.elapsedMs += deltaMs;
    this.spawnTimerMs += deltaMs;
    this.spawnBossIfNeeded(timeMs);

    if (this.spawnTimerMs >= this.getSpawnIntervalMs()) {
      this.spawnTimerMs = 0;
      this.spawnEnemy(timeMs);
    }

    for (const enemy of this.enemies) {
      if (!enemy.active) {
        continue;
      }

      if (enemy instanceof BossToken) {
        this.updateBoss(enemy, timeMs);
      } else {
        this.updateEnemy(enemy, timeMs);
      }
      this.recycleIfFarOutside(enemy);
    }

    this.bossSystem.updateEffects(timeMs);
  }

  getActiveCount() {
    return this.enemies.filter((enemy) => enemy.active).length;
  }

  getPoolSize() {
    return this.enemies.length;
  }

  getActiveEnemies() {
    return this.enemies.filter((enemy) => enemy.active);
  }

  getActiveBossThreats() {
    return this.bossSystem.getActiveThreats();
  }

  stopAll() {
    for (const enemy of this.enemies) {
      enemy.body.setVelocity(0, 0);
    }
    this.bossSystem.stopAll();
  }

  startWave(wave: number) {
    if (!Number.isInteger(wave) || wave < 1) {
      throw new Error(`Invalid enemy wave: ${wave}`);
    }
    if (wave === 1) {
      this.bossSpawnedForWave = 0;
    }
    this.wave = wave;
    this.elapsedMs = 0;
    this.spawnTimerMs = 0;
    this.waveToken += 1;
    this.pickEliteDueAtMs(wave);
    this.clearActiveEnemies(false);
    this.bossSystem.clearEffects();
  }

  clearAll() {
    this.clearActiveEnemies(true);
    this.bossSystem.clearEffects();
  }

  spawnBossForDebug(type: BossType, timeMs = this.scene.time.now) {
    const boss = this.bossSystem.spawnBoss(type, GAME_WIDTH / 2, 132, timeMs);
    this.markBossSpawn(boss, 'debug');
    boss.maxHp *= this.debugBossHpMultiplier;
    boss.hp = boss.maxHp;
    boss.speed *= this.debugBossSpeedMultiplier;
    boss.setData('collisionDamage', ((boss.getData('collisionDamage') as number | undefined) ?? 0) * getTunables().bossLab.damageMultiplier);
    return boss;
  }

  spawnMultipleBossesForDebug(type: BossType, count: number, timeMs = this.scene.time.now) {
    if (!Number.isInteger(count) || count < 1 || count > 6) {
      throw new Error(`Invalid debug boss count: ${count}`);
    }
    const bosses: BossToken[] = [];
    for (let index = 0; index < count; index += 1) {
      const boss = this.bossSystem.spawnBoss(
        type,
        Phaser.Math.Clamp(GAME_WIDTH / 2 + (index - (count - 1) / 2) * 52, 36, GAME_WIDTH - 36),
        132 + (index % 2) * 44,
        timeMs,
      );
      this.markBossSpawn(boss, 'debug');
      boss.maxHp *= this.debugBossHpMultiplier;
      boss.hp = boss.maxHp;
      boss.speed *= this.debugBossSpeedMultiplier;
      boss.setData('collisionDamage', ((boss.getData('collisionDamage') as number | undefined) ?? 0) * getTunables().bossLab.damageMultiplier);
      bosses.push(boss);
    }
    return bosses;
  }

  clearBossesForDebug() {
    for (const enemy of this.enemies) {
      if (enemy.active && enemy instanceof BossToken) {
        enemy.deactivate();
      }
    }
    this.bossSystem.clearEffects();
  }

  setDebugBossMultipliers(hpMultiplier: number, speedMultiplier: number) {
    if (!Number.isFinite(hpMultiplier) || hpMultiplier <= 0) {
      throw new Error(`Invalid debug boss HP multiplier: ${hpMultiplier}`);
    }
    if (!Number.isFinite(speedMultiplier) || speedMultiplier <= 0) {
      throw new Error(`Invalid debug boss speed multiplier: ${speedMultiplier}`);
    }
    this.debugBossHpMultiplier = hpMultiplier;
    this.debugBossSpeedMultiplier = speedMultiplier;
  }

  private clearActiveEnemies(cancelPendingSpawns: boolean) {
    if (cancelPendingSpawns) {
      this.waveToken += 1;
    }

    for (const enemy of this.enemies) {
      if (enemy.active) {
        enemy.deactivate();
      }
    }
  }

  private spawnEnemy(timeMs: number) {
    if (this.wave === 10 && this.elapsedMs < 2200) {
      return;
    }

    if (this.getActiveCount() >= this.getMaxActiveEnemies()) {
      return;
    }

    const definition = this.pickDefinition();
    const spawnPoint = this.pickSpawnPoint();
    const elite = this.shouldSpawnEliteNow();

    if (definition.kind === 'shooter' || definition.kind === 'burster') {
      const token = this.waveToken;
      this.showEdgeWarning(spawnPoint);
      this.scene.time.delayedCall(500, () => {
        if (token === this.waveToken) {
          this.activateEnemy(definition, spawnPoint, timeMs, elite);
        }
      });
      return;
    }

    this.activateEnemy(definition, spawnPoint, timeMs, elite);
  }

  private activateEnemy(definition: EnemyDefinition, spawnPoint: SpawnPoint, timeMs: number, elite = false) {
    if (this.getActiveCount() >= this.getMaxActiveEnemies()) {
      return;
    }

    const enemy = this.getEnemyFromPool();
    const turn = (this.rng?.next() ?? Phaser.Math.RND.frac()) > 0.5 ? 1 : -1;
    enemy.activate(definition, spawnPoint.x, spawnPoint.y, timeMs * 0.004, turn);
    enemy.setData('damageSource', 'collision');
    enemy.setData('collisionDamage', 12 * getTunables().enemies.collisionDamageMultiplier);
    enemy.setData('elite', false);

    if (elite) {
      this.applyEliteModifiers(enemy);
      this.eliteSpawnedForWave = this.wave;
    }
  }

  private getEnemyFromPool() {
    const inactive = this.enemies.find((enemy) => !enemy.active && !(enemy instanceof BossToken));
    if (inactive) {
      return inactive;
    }

    const enemy = new EnemyToken(this.scene);
    this.scene.add.existing(enemy);
    this.scene.physics.add.existing(enemy);
    enemy.setDepth(12);
    enemy.body.setAllowGravity(false);
    enemy.deactivate();
    this.enemies.push(enemy);
    return enemy;
  }

  private getBossFromPool() {
    const inactive = this.enemies.find((enemy) => !enemy.active && enemy instanceof BossToken) as BossToken | undefined;
    if (inactive) {
      return inactive;
    }

    const boss = new BossToken(this.scene);
    this.scene.add.existing(boss);
    this.scene.physics.add.existing(boss);
    boss.body.setAllowGravity(false);
    boss.deactivate();
    this.enemies.push(boss);
    return boss;
  }

  private pickEliteDueAtMs(wave: number) {
    const specialDrops = getTunables().specialDrops;
    this.eliteDueAtMs = Infinity;
    if (wave < specialDrops.eliteMinWave || this.rngValue() >= specialDrops.eliteChancePerWave) {
      return;
    }
    this.eliteDueAtMs = this.randomBetween(4500, 14000);
  }

  private shouldSpawnEliteNow() {
    if (this.eliteSpawnedForWave === this.wave || this.elapsedMs < this.eliteDueAtMs) {
      return false;
    }
    this.eliteSpawnedForWave = this.wave;
    return true;
  }

  private applyEliteModifiers(enemy: EnemyToken) {
    const specialDrops = getTunables().specialDrops;
    enemy.hp *= specialDrops.eliteHpMultiplier;
    enemy.setData('maxHp', enemy.hp);
    enemy.speed *= specialDrops.eliteSpeedMultiplier;
    enemy.setData('elite', true);
    enemy.setFillStyle(0xffd34d, 1);
    enemy.setStrokeStyle(3, 0xfff3b0, 1);
    enemy.setScale(1.2);
    enemy.body.setCircle(enemy.radius * 1.1);
    enemy.body.setOffset(-enemy.radius * 1.1, -enemy.radius * 1.1);
    this.showEliteLabel(enemy.x, enemy.y);
  }

  private showEliteLabel(x: number, y: number) {
    const label = this.scene.add.text(x, y - 24, '精英', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      color: '#fff2b0',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(60);
    this.scene.tweens.add({
      targets: label,
      y: y - 42,
      alpha: 0,
      duration: 900,
      onComplete: () => label.destroy(),
    });
  }

  private pickDefinition() {
    const available = this.spawnWeights
      .map((kind) => ENEMY_DEFINITIONS[kind])
      .filter((definition) => this.elapsedMs >= definition.unlockAtMs * getTunables().enemies.specialUnlockTimeMultiplier);

    return this.rng ? this.rng.pick(available) : Phaser.Utils.Array.GetRandom(available);
  }

  private pickSpawnPoint(): SpawnPoint {
    const playerPosition = new Phaser.Math.Vector2(this.player.x, this.player.y);

    for (let attempt = 0; attempt < 16; attempt += 1) {
      const side = this.pickSide();
      const point = this.pointForSide(side);

      if (Phaser.Math.Distance.Between(point.x, point.y, playerPosition.x, playerPosition.y) >= ENEMY_MIN_PLAYER_DISTANCE) {
        return point;
      }
    }

    const fallbackSide = this.player.y < GAME_HEIGHT / 2 ? 'bottom' : 'top';
    return this.pointForSide(fallbackSide);
  }

  private pointForSide(side: SpawnPoint['side']): SpawnPoint {
    if (side === 'top') {
      return {
        x: this.randomBetween(20, GAME_WIDTH - 20),
        y: -ENEMY_SPAWN_MARGIN,
        side,
      };
    }

    if (side === 'bottom') {
      return {
        x: this.randomBetween(20, GAME_WIDTH - 20),
        y: GAME_HEIGHT + ENEMY_SPAWN_MARGIN,
        side,
      };
    }

    const y = this.randomBetween(92, GAME_HEIGHT - 92);
    return {
      x: side === 'left' ? -ENEMY_SPAWN_MARGIN - 26 : GAME_WIDTH + ENEMY_SPAWN_MARGIN + 26,
      y,
      side,
    };
  }

  private updateEnemy(enemy: EnemyToken, timeMs: number) {
    if ((enemy.getData('stunnedUntil') as number | undefined ?? 0) > timeMs) {
      enemy.body.setVelocity(0, 0);
      return;
    }

    const toPlayer = new Phaser.Math.Vector2(this.player.x - enemy.x, this.player.y - enemy.y);

    if (toPlayer.lengthSq() <= 1) {
      enemy.body.setVelocity(0, 0);
      return;
    }

    toPlayer.normalize();

    if (enemy.kind === 'flanker') {
      const wobble = Math.sin(timeMs * 0.004 + enemy.phase) * 0.65;
      const perpendicular = new Phaser.Math.Vector2(-toPlayer.y, toPlayer.x).scale(wobble * enemy.turn);
      toPlayer.add(perpendicular).normalize();
    }

    if (enemy.kind === 'shooter') {
      const distance = Phaser.Math.Distance.Between(enemy.x, enemy.y, this.player.x, this.player.y);
      if (distance < 165) {
        toPlayer.negate();
      }
      enemy.setScale(1 + Math.sin(timeMs * 0.006 + enemy.phase) * 0.12);
    } else if (enemy.kind === 'burster') {
      enemy.setScale(1 + Math.sin(timeMs * 0.012 + enemy.phase) * 0.18);
    } else {
      enemy.setScale(1);
    }

    if (enemy.getData('elite') === true) {
      enemy.setScale(enemy.scaleX * 1.18, enemy.scaleY * 1.18);
    }

    const slowUntil = (enemy.getData('slowUntil') as number | undefined) ?? 0;
    const slowAmount = (enemy.getData('slowAmount') as number | undefined) ?? 0;
    const speedMultiplier = slowUntil > timeMs ? Math.max(0.1, 1 - slowAmount) : 1;
    enemy.body.setVelocity(toPlayer.x * enemy.speed * speedMultiplier, toPlayer.y * enemy.speed * speedMultiplier);
  }

  private spawnBossIfNeeded(timeMs: number) {
    const bossPool = getBossPoolForWave(this.wave);
    if (!bossPool || this.bossSpawnedForWave === this.wave) {
      return;
    }

    const type = bossPool.length === 1
      ? bossPool[0]
      : (this.rng?.pick(bossPool) ?? bossPool[Phaser.Math.Between(0, bossPool.length - 1)]);
    const boss = this.bossSystem.spawnBoss(type, GAME_WIDTH / 2, 132, timeMs);
    this.markBossSpawn(boss, 'wave');
    this.bossSpawnedForWave = this.wave;
  }

  private markBossSpawn(boss: BossToken, mode: BossSpawnMode) {
    boss.setData('bossSpawnMode', mode);
    boss.setData('bossWave', this.wave);
  }

  private updateBoss(boss: BossToken, timeMs: number) {
    this.bossSystem.updateBoss(boss, timeMs);
  }

  private activateSummonedMinion(spawnPoint: SpawnPoint, timeMs: number) {
    if (this.getActiveCount() >= this.getMaxActiveEnemies()) {
      return;
    }
    const enemy = this.getEnemyFromPool();
    const turn = (this.rng?.next() ?? Phaser.Math.RND.frac()) > 0.5 ? 1 : -1;
    enemy.activate(ENEMY_DEFINITIONS.charger, spawnPoint.x, spawnPoint.y, timeMs * 0.004, turn);
    enemy.hp = getTunables().boss.minionHp;
    enemy.speed = getTunables().boss.minionSpeed;
    enemy.setFillStyle(0xff5c5c, 1);
    enemy.setStrokeStyle(2, 0xffdfcf, 0.92);
    enemy.setData('damageSource', 'minion');
    enemy.setData('collisionDamage', getTunables().boss.minionCollisionDamage);
    enemy.setData('elite', false);
  }

  private getActiveBossMinionCount() {
    return this.enemies.filter((enemy) => enemy.active && !(enemy instanceof BossToken) && enemy.getData('damageSource') === 'minion').length;
  }

  private recycleIfFarOutside(enemy: EnemyToken) {
    if (enemy instanceof BossToken) {
      return;
    }
    const margin = 150;
    if (
      enemy.x < -margin ||
      enemy.x > GAME_WIDTH + margin ||
      enemy.y < -margin ||
      enemy.y > GAME_HEIGHT + margin
    ) {
      enemy.deactivate();
    }
  }

  private showEdgeWarning(spawnPoint: SpawnPoint) {
    const warning = this.scene.add.triangle(
      Phaser.Math.Clamp(spawnPoint.x, 22, GAME_WIDTH - 22),
      Phaser.Math.Clamp(spawnPoint.y, 22, GAME_HEIGHT - 22),
      0,
      -10,
      12,
      10,
      -12,
      10,
      0xffd26d,
      0.92,
    );
    warning.setDepth(40);
    warning.setRotation(this.rotationForWarning(spawnPoint.side));

    this.scene.tweens.add({
      targets: warning,
      alpha: 0.15,
      scale: 1.35,
      duration: 250,
      yoyo: true,
      repeat: 1,
      onComplete: () => warning.destroy(),
    });
  }

  private rotationForWarning(side: SpawnPoint['side']) {
    if (side === 'top') {
      return Math.PI;
    }

    if (side === 'bottom') {
      return 0;
    }

    return side === 'left' ? Math.PI / 2 : -Math.PI / 2;
  }

  private pickSide(): SpawnPoint['side'] {
    return this.rng
      ? this.rng.pick(['top', 'right', 'bottom', 'left'] as const)
      : Phaser.Utils.Array.GetRandom(['top', 'right', 'bottom', 'left'] as const);
  }

  private randomBetween(min: number, max: number) {
    return this.rng?.integer(min, max) ?? Phaser.Math.Between(min, max);
  }

  private rngValue() {
    return this.rng?.next() ?? Phaser.Math.RND.frac();
  }

  private getSpawnIntervalMs() {
    const enemies = getTunables().enemies;
    return Math.max(
      enemies.minSpawnIntervalMs,
      (enemies.spawnIntervalBaseMs - (this.wave - 1) * enemies.spawnIntervalStepMs) * enemies.spawnIntervalMultiplier,
    );
  }

  private getMaxActiveEnemies() {
    const enemies = getTunables().enemies;
    return Math.min(ENEMY_MAX_ACTIVE + 60, Math.round(enemies.maxActiveBase + (this.wave - 1) * enemies.maxActivePerWave));
  }

  getEnemies() {
    return this.enemies;
  }

  getNearestEnemy(x: number, y: number): EnemyToken | null {
    let nearest = null;
    let minDistSq = Infinity;
    for (const enemy of this.enemies) {
      if (enemy.active) {
        const distSq = Phaser.Math.Distance.Squared(x, y, enemy.x, enemy.y);
        if (distSq < minDistSq) {
          minDistSq = distSq;
          nearest = enemy;
        }
      }
    }
    return nearest;
  }

  takeDamage(enemy: EnemyToken, amount: number, sourceType?: PartnerType | 'relic') {
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error(`Invalid enemy damage amount: ${amount}`);
    }

    const resolvedDamage = enemy instanceof BossToken
      ? this.bossSystem.resolveIncomingDamage(enemy, amount, sourceType, this.rng?.next() ?? Phaser.Math.RND.frac())
      : amount;
    if (resolvedDamage <= 0) {
      return false;
    }

    this.scene.events.emit('enemy-damaged', {
      amount: resolvedDamage,
      source: sourceType,
      x: enemy.x,
      y: enemy.y,
    } satisfies EnemyDamagePayload);
    enemy.hp -= resolvedDamage;
    if (enemy.hp <= 0) {
      const deathX = enemy.x;
      const deathY = enemy.y;
      const wasBoss = enemy instanceof BossToken;
      const wasElite = !wasBoss && enemy.getData('elite') === true;
      const bossDeathPayload = wasBoss
        ? {
            x: deathX,
            y: deathY,
            type: (enemy as BossToken).bossType,
            mode: enemy.getData('bossSpawnMode') as BossSpawnMode | undefined,
            wave: enemy.getData('bossWave') as number | undefined,
            source: sourceType,
          }
        : undefined;
      enemy.deactivate();
      if (wasBoss) {
        this.scene.events.emit('boss-died', bossDeathPayload);
      } else {
        if (wasElite) {
          this.scene.events.emit('elite-died', deathX, deathY);
        }
        this.scene.events.emit('enemy-died', deathX, deathY, sourceType);
        this.scene.events.emit('enemy-death-stats', {
          x: deathX,
          y: deathY,
          source: sourceType,
        } satisfies EnemyDeathPayload);
      }
      return true;
    }
    return false;
  }
}

function getBossPoolForWave(wave: number) {
  return BOSS_SCHEDULE[wave] ?? (wave > 10 && wave % 3 === 0 ? ENDLESS_BOSS_POOL : undefined);
}
